import React, { useState, useEffect } from 'react';
import { Input, Label, FormGroup, Button } from 'reactstrap';
import { Colors } from '../../../../utils/budget-tool-specific';

const FlightProviderToggle = ({ provider, onProviderChange, disabled, isSearching }) => {
    const [connectionStatus, setConnectionStatus] = useState({
        flightapi: 'unknown',
        amadeus: 'unknown'
    });
    const [testing, setTesting] = useState(false);

    const providerInfo = {
        flightapi: {
            name: 'FlightApi.io',
            description: 'Current production flight data provider',
            status: 'stable',
            icon: '✈️'
        },
        amadeus: {
            name: 'Amadeus',
            description: 'Alternative flight data provider with global coverage',
            status: 'experimental',
            icon: '🌍'
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'connected': return '#28a745';
            case 'error': return '#dc3545';
            case 'testing': return '#ffc107';
            default: return '#6c757d';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'connected': return '✅';
            case 'error': return '❌';
            case 'testing': return '🔄';
            default: return '❓';
        }
    };

    const testConnection = async (providerType) => {
        setTesting(true);
        setConnectionStatus(prev => ({ ...prev, [providerType]: 'testing' }));

        try {
            // Import the service dynamically to test connection
            const { FlightAPIService } = await import('../services/FlightAPIService');
            const testService = new FlightAPIService(providerType);

            // Try to get provider info (basic connection test)
            const info = testService.providerInstance.getInfo();
            if (info) {
                setConnectionStatus(prev => ({ ...prev, [providerType]: 'connected' }));
            } else {
                setConnectionStatus(prev => ({ ...prev, [providerType]: 'error' }));
            }
        } catch (error) {
            console.error(`Connection test failed for ${providerType}:`, error);
            setConnectionStatus(prev => ({ ...prev, [providerType]: 'error' }));
        } finally {
            setTesting(false);
        }
    };

    const handleProviderChange = (newProvider) => {
        if (disabled || isSearching) return;
        onProviderChange(newProvider);
    };

    return (
        <div style={{
            background: "linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)",
            borderRadius: "12px",
            padding: "20px",
            border: "2px solid #ffc107",
            marginBottom: "20px",
            boxShadow: "0 2px 8px rgba(255,193,7,0.2)"
        }}>
            <div style={{marginBottom: "15px"}}>
                <Label style={{
                    fontWeight: "600",
                    color: Colors.primary,
                    fontSize: "16px",
                    marginBottom: "5px"
                }}>
                    🚀 Flight Data Source (Experimental)
                </Label>
                <p style={{
                    fontSize: "14px",
                    color: "#6c757d",
                    margin: "5px 0 0 0",
                    lineHeight: "1.4"
                }}>
                    Switch between different flight data providers to compare results and features.
                </p>
            </div>

            {/* Provider Selection */}
            <FormGroup>
                <div style={{display: "flex", gap: "15px", marginBottom: "15px"}}>
                    {Object.entries(providerInfo).map(([key, info]) => (
                        <div
                            key={key}
                            onClick={() => handleProviderChange(key)}
                            style={{
                                flex: 1,
                                border: `2px solid ${provider === key ? '#ffc107' : '#e9ecef'}`,
                                borderRadius: "8px",
                                padding: "15px",
                                backgroundColor: provider === key ? 'rgba(255,193,7,0.1)' : 'white',
                                cursor: disabled || isSearching ? 'not-allowed' : 'pointer',
                                transition: "all 0.2s ease",
                                opacity: disabled || isSearching ? 0.6 : 1
                            }}
                            onMouseEnter={(e) => {
                                if (!disabled && !isSearching && provider !== key) {
                                    e.currentTarget.style.borderColor = '#ffc107';
                                    e.currentTarget.style.backgroundColor = 'rgba(255,193,7,0.05)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!disabled && !isSearching && provider !== key) {
                                    e.currentTarget.style.borderColor = '#e9ecef';
                                    e.currentTarget.style.backgroundColor = 'white';
                                }
                            }}
                        >
                            <div style={{display: "flex", alignItems: "center", marginBottom: "8px"}}>
                                <span style={{fontSize: "20px", marginRight: "8px"}}>
                                    {info.icon}
                                </span>
                                <strong style={{color: Colors.primary, fontSize: "14px"}}>
                                    {info.name}
                                </strong>
                                {provider === key && (
                                    <span style={{
                                        marginLeft: "auto",
                                        backgroundColor: Colors.primaryAccent,
                                        color: Colors.primaryDark,
                                        padding: "2px 8px",
                                        borderRadius: "10px",
                                        fontSize: "11px",
                                        fontWeight: "600"
                                    }}>
                                        ACTIVE
                                    </span>
                                )}
                            </div>
                            <p style={{
                                fontSize: "12px",
                                color: "#6c757d",
                                margin: "0 0 8px 0",
                                lineHeight: "1.3"
                            }}>
                                {info.description}
                            </p>
                            <div style={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                                <span style={{
                                    fontSize: "11px",
                                    color: info.status === 'stable' ? '#28a745' : '#ffc107',
                                    fontWeight: "600",
                                    textTransform: "uppercase"
                                }}>
                                    {info.status}
                                </span>
                                <div style={{display: "flex", alignItems: "center"}}>
                                    <span style={{marginRight: "4px"}}>
                                        {getStatusIcon(connectionStatus[key])}
                                    </span>
                                    <Button
                                        size="sm"
                                        color="link"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            testConnection(key);
                                        }}
                                        disabled={testing}
                                        style={{
                                            fontSize: "10px",
                                            padding: "2px 6px",
                                            color: Colors.primary,
                                            textDecoration: "none"
                                        }}
                                    >
                                        Test
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </FormGroup>

            {/* Status Information */}
            {isSearching && (
                <div style={{
                    fontSize: "12px",
                    color: "#0c5460",
                    backgroundColor: "#d1ecf1",
                    border: "1px solid #bee5eb",
                    borderRadius: "6px",
                    padding: "8px 12px",
                    marginTop: "10px"
                }}>
                    🔄 Searching with {providerInfo[provider]?.name}...
                </div>
            )}

            {/* Warning */}
            <div style={{
                fontSize: "12px",
                color: "#856404",
                backgroundColor: "rgba(255,193,7,0.1)",
                padding: "8px 12px",
                borderRadius: "6px",
                marginTop: "10px"
            }}>
                ⚠️ <strong>Experimental Feature:</strong> Results may vary between providers.
                {provider === 'amadeus' && (
                    <span> Amadeus requires API credentials in environment variables.</span>
                )}
            </div>

            {/* Provider Details */}
            {provider && (
                <div style={{
                    marginTop: "15px",
                    padding: "10px",
                    backgroundColor: "rgba(255,255,255,0.7)",
                    borderRadius: "6px",
                    border: "1px solid rgba(255,193,7,0.3)"
                }}>
                    <div style={{fontSize: "12px", color: Colors.primary}}>
                        <strong>Current Provider: {providerInfo[provider]?.name}</strong>
                        <br />
                        Status: <span style={{color: getStatusColor(connectionStatus[provider])}}>
                            {connectionStatus[provider] === 'connected' ? 'Ready' :
                             connectionStatus[provider] === 'error' ? 'Connection Issue' :
                             connectionStatus[provider] === 'testing' ? 'Testing...' :
                             'Unknown'}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FlightProviderToggle;